import {
  OnboardingData,
  RaceEvent,
  UserDistance,
  TrainingDistance,
  CommunityParticipation,
  TrainingIntensity,
} from '../types/user';
import {
  DistanceUnit,
  ExperienceLevel,
  RunningCommunity,
} from '../types/enums';
import { OnboardingSessionData } from '../types/onboardingSession';

/**
 * Build OnboardingSessionData from raw onboarding answers.
 * Used by both the onboarding hook and the Signup page (fallback from sessionStorage).
 */
export function buildOnboardingSessionData(answers: Record<string, any>): OnboardingSessionData {
  // Parse name
  const fullName: string = answers.initial_onboarding || '';
  const nameParts = fullName.trim().split(/\s+/);
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || undefined;

  // Build training distance
  let trainingDistance: string | TrainingDistance | undefined;
  const programme = answers.programme_type;
  if (programme) {
    const dist = answers.training_distance;
    trainingDistance = {
      name: programme,
      distance: dist?.distance,
      unit: dist?.unit,
    };
  }

  // Build community participation
  let communityParticipation: CommunityParticipation | undefined;
  const community = answers.running_community_participation;
  if (community && community !== RunningCommunity.NONE) {
    communityParticipation = {
      type: community,
      distance:
        community === RunningCommunity.RUN_CLUB
          ? answers.run_club_distance
          : ({ distance: 5, unit: DistanceUnit.KM } as UserDistance),
      day: answers.run_club_day,
      difficulty: answers.run_club_intensity,
    };
  }

  // Build training intensity
  const intensityLevel = answers.training_intensity_preference;
  const trainingIntensity: TrainingIntensity = {
    level: typeof intensityLevel === 'number' ? intensityLevel : undefined,
    days: answers.intensive_workout_days_preference,
  };

  // Goal time / PB — extract seconds or undefined
  const parseTimeAnswer = (val: any): number | undefined => {
    if (typeof val === 'number') return val;
    return undefined; // skip option was selected (string)
  };

  // Longest run distance
  const recentHistory = answers.recent_running_history;
  const longestRunDistance: UserDistance | undefined =
    recentHistory && typeof recentHistory === 'object'
      ? recentHistory
      : undefined;

  // Start date
  const startDate: string =
    answers.training_start_date ||
    new Date().toISOString().split('T')[0];

  // Build race event
  let raceEvent: RaceEvent | undefined;
  const raceSelection = answers.race_selection;
  if (raceSelection && typeof raceSelection === 'object') {
    raceEvent = {
      id: raceSelection.id,
      title: raceSelection.title,
      location: raceSelection.location,
      startDate: raceSelection.startDate || answers.race_date,
    };
  }

  const onboardingData: OnboardingData = {
    training_purpose: answers.training_purpose,
    training_distance: trainingDistance,
    experience_level: answers.experience_level as ExperienceLevel,
    longest_run_distance: longestRunDistance,
    longest_run_time: parseTimeAnswer(answers.recent_longest_run_time),
    goal_time: parseTimeAnswer(answers.goal_time),
    current_pb: parseTimeAnswer(answers.current_pb),
    longest_run_day: answers.long_run_day_preference,
    training_intensity: trainingIntensity,
    community_participation: communityParticipation,
    number_of_workouts: answers.weekly_training_frequency,
    number_of_weeks: answers.training_duration,
    days_cant_train: answers.training_availability,
    distance_unit: answers.distance_unit_preference as DistanceUnit,
    additional_info: answers.additional_information,
  };

  return {
    first_name: firstName,
    last_name: lastName,
    gender: answers.gender || '',
    birth_date: answers.date_of_birth || '',
    onboarding_data: onboardingData,
    start_date: startDate,
    race_event: raceEvent,
    is_new_plan: true,
  };
}
